/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-floating-promises */
/**
 * ChatFlowController V2 - Refactored version
 * Clean architecture with better separation of concerns
 *
 * WHY CLASS-BASED APPROACH WAS COMPULSORY (NOT OPTIONAL):
 *
 * 1. PERSISTENT INSTANCE STATE: The controller needs to maintain state between
 *    multiple React renders and hook calls. Functions would lose state on each
 *    call, requiring complex external state management.
 *
 * 2. METHOD BINDING: The hook calls methods like `controller.handleFormSubmit()`
 *    expecting them to be bound to the same instance. Functional approach would
 *    require complex closure patterns or external binding.
 */

/* eslint-disable class-methods-use-this, @typescript-eslint/no-unused-vars, no-unused-vars */

import type { Dispatch } from '@reduxjs/toolkit';

import { type RootState } from '../../../services/serviceTypes';
import {
  // Step constants
  MESSAGE_SUBTYPES,
  STEP_NAMES,
} from '../constants/steps';
import type { CopilotState } from '../data-layer/types';
import {
  addCompletedStep,
  addMessage,
  removeMessage,
  removeMessagesBetweenSteps,
  removeMessagesBySubType,
  removeSkippedStep,
  setActiveStep,
  setConnectionPrompt,
  setEntitlementSteps,
  setParsedTemplates,
  setSavedStepData,
  updateNameToLabelMap,
  updateStepFormData,
} from '../data-store/copilotSlice';
import { saveAccountConfigurationForEdit } from '../services';
import ConfirmationService from '../services/confirmationService';
import {
  createAccountHelperMessage,
  showError,
} from '../services/messageService';
import SkipService from '../services/skipService';
import { handleAccountSubmit } from '../utils/accountSubmissionUtils';
import AddAccessController from '../utils/addAccessUtils';
import { handleButtonClick } from '../utils/buttonHandlerUtils';
import {
  handleContinueWithInstructions,
  handleModifyInstructions,
} from '../utils/buttonResponseHandlers';
import { handleConnectionSubmit } from '../utils/connectionSubmissionUtils';
import { buildConnectionPayload } from '../utils/connectionUtils';
import { getEndpointKey } from '../utils/endpointKeyUtils';
import handlePreviousStepSubmit from '../utils/formSubmissionRouter';
import cleanupStepMessages from '../utils/messageCleanupUtils';
import { isEditMode, parseUrlParameters } from '../utils/modeUtils';
import isEditingPreviousStep from '../utils/previousStepDetectionUtils';
import RemoveAccessController from '../utils/removeAccessUtils';
import STEP_CONFIGS from '../utils/stepConfigs';
import showStepForm from '../utils/stepFormDisplayUtils';
import { showRemoveAccessForm } from '../utils/stepFormUtils';
import {
  handleAccountHelpNo,
  handleAccountHelpYes,
  handleStepHelpNo,
  handleStepHelpYes,
} from '../utils/stepHelpUtils';
import {
  initializeCreateMode,
  initializeEditMode,
} from '../utils/stepInitializationUtils';
import navigateToConnection from '../utils/stepNavigationUtils';
import type { ParsedTemplateData } from '../utils/templateParsers';

interface ControllerConfig {
  dispatch: Dispatch;
  getState: () => unknown;
  history: {
    push: (path: string) => void;
  };
  services: {
    initiateOnboard: (payload: unknown) => Promise<unknown>;
    testConnection: (payload: unknown) => Promise<unknown>;
    saveUseCaseConfiguration: (payload: unknown) => Promise<unknown>;
    updateApplication: (payload: unknown) => Promise<unknown>;
    generateCategoryTemplate: (payload: unknown) => Promise<unknown>;
  };
}

export default class ChatFlowController {
  private dispatch: Dispatch;

  private originalDispatch: Dispatch;

  private getState: () => unknown;

  private history: ControllerConfig['history'];

  private services: ControllerConfig['services'];

  private addAccessController: AddAccessController;

  private removeAccessController: RemoveAccessController;

  private skipService: SkipService;

  private confirmationService: ConfirmationService;

  private isInitialized = false;

  constructor(config: ControllerConfig) {
    this.dispatch = config.dispatch;
    this.originalDispatch = config.dispatch;
    this.getState = config.getState;
    this.history = config.history;
    this.services = config.services;

    // Initialize AddAccess controller with exact v1 logic
    this.addAccessController = new AddAccessController({
      dispatch: this.dispatch,
      getState: this.getState,
      services: {
        saveUseCaseConfiguration: this.services.saveUseCaseConfiguration,
      },
    });

    // Initialize RemoveAccess controller
    this.removeAccessController = new RemoveAccessController({
      dispatch: this.dispatch,
      getState: this.getState,
      services: {
        saveUseCaseConfiguration: this.services.saveUseCaseConfiguration,
      },
    });

    // Initialize Skip service
    this.skipService = new SkipService({
      dispatch: this.dispatch,
      getState: this.getState,
    });

    // Initialize Confirmation service
    this.confirmationService = new ConfirmationService({
      dispatch: this.dispatch,
      getState: this.getState,
    });

    // Set callback for final confirmation
    this.removeAccessController.showFinalConfirmation = () => {
      this.showFinalConfirmation();
    };

    // Set skip service callback
    this.skipService.showFinalConfirmation = () => {
      this.showFinalConfirmation();
    };

    // Set step form callback for skip navigation
    this.skipService.showStepForm = (stepName: string) => {
      this.showStepFormByName(stepName);
    };

    // Connect removeAccessController to skipService
    this.skipService.removeAccessController = this.removeAccessController;

    // Set callbacks for AddAccess controller
    this.addAccessController.showNextEntitlementForm = (
      index,
      entitlementName
    ) => {
      this.showEntitlementForm(index, entitlementName);
    };

    this.addAccessController.moveToRemoveAccess = () => {
      if (!isEditMode(this.getState)) {
        this.moveToRemoveAccess();
      } else {
        this.dispatch(addCompletedStep('AddAccess'));
        this.dispatch(removeSkippedStep('AddAccess'));

        // Get the current state to check messages
        const state = this.getState() as RootState;

        const successMessageExists = state.copilot.messages.some(
          (msg) => msg.subType === 'addAccessUpdateSuccess'
        );

        // Only add the message if it doesn't already exist
        if (!successMessageExists) {
          this.dispatch(
            addMessage({
              id: Date.now().toString(),
              type: 'ai_response',
              subType: 'addAccessUpdateSuccess',
              text: 'You have updated the Add Access Entitlements. I will add to the Configuration.',
              timestamp: Date.now(),
            })
          );
        }
      }
    };

    this.addAccessController.showFinalConfirmation = () => {
      this.showFinalConfirmation();
    };

    // Wrap dispatch to add auto-scroll functionality
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    this.dispatch = (action: any) => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const result = this.originalDispatch(action);

      // eslint-disable-next-line @typescript-eslint/no-unsafe-return
      return result;
    };

    parseUrlParameters(this.dispatch);
  }

  initialize(
    nameToLabelMap: Record<string, string>,
    parsedTemplates: ParsedTemplateData
  ): void {
    // Prevent multiple initializations
    if (this.isInitialized) {
      return;
    }

    this.isInitialized = true;

    if (isEditMode(this.getState)) {
      initializeEditMode(this.dispatch, this.getState, nameToLabelMap);
    } else {
      initializeCreateMode(this.dispatch, this.getState);
    }
  }

  private clearSuccessErrorMessages(): void {
    const state = this.getState() as { copilot?: CopilotState };
    const messages = state.copilot?.messages || [];
    const successOrErrorMessages = messages.filter(
      (msg) => msg.subType === 'success' || msg.subType === 'error'
    );

    successOrErrorMessages.forEach((msg) => {
      this.dispatch(removeMessage(msg.id));
    });
  }

  private showPreviousSavedPrompt(): void {
    this.clearSuccessErrorMessages();

    const state = this.getState() as { copilot?: CopilotState };

    const parsedTemplates = state.copilot?.parsedTemplates || {};
    const stepData = parsedTemplates.Connection as
      | { instructions?: string }
      | undefined;
    const connectionPrompt = stepData?.instructions || '';

    if (connectionPrompt !== '' && connectionPrompt.trim()) {
      // Show the stored connection prompt with Yes/No buttons
      const connectionInstructionMessage = {
        id: `connection-edit-instruction-${Date.now()}`,
        type: 'ai_response' as const,
        subType: MESSAGE_SUBTYPES.INSTRUCTION_PROMPT,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        text: `Hey, it seems you've already provided the additional information.\n\n${connectionPrompt}\n\nDo you want to continue with above instruction?`,
        timestamp: Date.now(),
        buttons: [
          { label: 'Yes', action: 'continue_with_instructions' },
          { label: 'No', action: 'connection_not_align_with_prev_instruction' },

          // { label: 'No', action: 'connection_modify_instruction' },
        ],
      };

      this.dispatch(addMessage(connectionInstructionMessage));
    } else {
      // No stored prompt, show the 3-button helper directly
      const connectionHelperMessage = {
        id: `connection-helper-edit-${Date.now()}`,
        type: 'ai_response' as const,
        subType: 'threeButtonsHelperText' as const,
        timestamp: Date.now(),
        buttons: [
          { label: 'Specify Prompt', action: 'connection_help_yes' },
          { label: 'Upload Browser Recording', action: 'connection_help_yes' },
          { label: 'Skip for Now', action: 'connection_save_call' },
        ],
      };

      this.dispatch(addMessage(connectionHelperMessage));
    }
  }

  async handleFormSubmit(
    stepName: string,
    formData: Record<string, unknown>,
    isValid: boolean
  ): Promise<void> {
    const accessRelatedStepName = [
      'SetEntitlementSteps',
      'AddAccess',
      'AddAccess',
      'AddAccessConfirmation',
      'RemoveAccessConfirmation',
      'RemoveAccess',
      'RemoveAccess',
    ];

    if (!isValid) {
      return;
    }

    this.dispatch(updateStepFormData({ stepName, formData }));
    this.dispatch(setSavedStepData({ stepName, formData }));

    // Handle previous step edit - no transitions, just show responses

    if (
      isEditMode(this.getState) &&
      !accessRelatedStepName.includes(stepName)
    ) {
      // Clear any previous success/error messages in edit mode
      this.clearSuccessErrorMessages();

      if (
        stepName === STEP_NAMES.CONNECTION ||
        stepName === STEP_NAMES.GENERAL
      ) {
        if (stepName === STEP_NAMES.CONNECTION) {
          // Clean up messages and show connection prompt
          this.showPreviousSavedPrompt();

          return;
        }

        handlePreviousStepSubmit(
          this.dispatch,
          stepName,
          formData,
          isValid,
          this.getState,
          {
            updateApplication: this.services.updateApplication,
          }
        );
      } else if (
        stepName === STEP_NAMES.UPDATE_ACCOUNT ||
        stepName === STEP_NAMES.CREATE_ACCOUNT ||
        stepName === STEP_NAMES.ENABLE_ACCOUNT ||
        stepName === STEP_NAMES.DISABLE_ACCOUNT ||
        stepName === STEP_NAMES.REMOVE_ACCOUNT
      ) {
        // Check for existing prompt first
        const currentState = this.getState() as {
          copilot?: {
            endpointKey?: number | null;
            ui?: { endpointKey?: string | null };
            parsedTemplates?: Record<string, { instructions?: string }>;
          };
        };

        const endpointKey = getEndpointKey(currentState.copilot!);
        const existingPrompt =
          currentState.copilot?.parsedTemplates?.[stepName]?.instructions ?? '';

        if (existingPrompt.trim()) {
          // Has prompt: Call saveUseCaseConfiguration
          if (endpointKey) {
            const jsonFieldMap: Record<string, string> = {
              CreateAccount: 'externalconnattvalue.CreateAccountJSON',
              UpdateAccount: 'externalconnattvalue.UpdateAccountJSON',
              EnableAccount: 'externalconnattvalue.EnableAccountJSON',
              DisableAccount: 'externalconnattvalue.DisableAccountJSON',
              RemoveAccount: 'externalconnattvalue.RemoveAccountJSON',
            };

            const jsonField = jsonFieldMap[stepName];
            const accountJSON = formData[jsonField];

            if (jsonField && typeof accountJSON === 'string') {
              try {
                // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-argument
                await saveAccountConfigurationForEdit(
                  this.services,
                  this.dispatch,
                  this.getState,
                  stepName,
                  accountJSON,
                  Number(endpointKey),
                  existingPrompt
                );
              } catch (error) {
                // Log error for debugging
                console.error('Account configuration save failed:', error);
              }
            }
          }
        } else {
          // No prompt: Show 3-button helper
          this.dispatch(
            addMessage(
              createAccountHelperMessage(
                MESSAGE_SUBTYPES.THREE_BUTTONS_HELPER_TEXT
              )
            )
          );
        }
      }

      return;
    }

    if (
      isEditingPreviousStep(stepName, this.getState) &&
      !accessRelatedStepName.includes(stepName)
    ) {
      // Clean up messages between current step and next step
      cleanupStepMessages(this.dispatch, stepName);

      handlePreviousStepSubmit(
        this.dispatch,
        stepName,
        formData,
        isValid,
        this.getState,
        {
          updateApplication: this.services.updateApplication,
        }
      );

      return;
    }

    // Original first visit logic
    if (stepName === STEP_NAMES.GENERAL) {
      this.dispatch(addCompletedStep(stepName));
      navigateToConnection(
        this.dispatch,
        this.getState,
        isEditMode(this.getState)
      );
    } else if (stepName === STEP_NAMES.CONNECTION) {
      handleConnectionSubmit(this.dispatch, formData, isValid, this.getState);
    } else if (
      stepName === STEP_NAMES.CREATE_ACCOUNT ||
      stepName === STEP_NAMES.UPDATE_ACCOUNT ||
      stepName === STEP_NAMES.ENABLE_ACCOUNT ||
      stepName === STEP_NAMES.DISABLE_ACCOUNT ||
      stepName === STEP_NAMES.REMOVE_ACCOUNT
    ) {
      handleAccountSubmit(
        this.dispatch,
        stepName as
          | 'CREATE_ACCOUNT'
          | 'UPDATE_ACCOUNT'
          | 'ENABLE_ACCOUNT'
          | 'DISABLE_ACCOUNT'
          | 'REMOVE_ACCOUNT',
        formData,
        isValid,
        this.getState
      );
    } else if (stepName === 'SetEntitlementSteps') {
      this.dispatch(
        setEntitlementSteps(
          formData.entitlements as { id: number; value: string }[]
        )
      );

      // Only show first entitlement form if no entitlement forms exist
      const state = this.getState() as {
        copilot?: { messages?: { type?: string; subType?: string }[] };
      };
      const hasEntitlementForms =
        state.copilot?.messages?.some(
          (msg) =>
            msg.type === 'step_form' && msg.subType === 'accessAttributeMapping'
        ) || false;

      if (!hasEntitlementForms) {
        this.showFirstEntitlementForm();
      }
    } else if (stepName === 'AddAccess') {
      this.dispatch(addCompletedStep('AddAccess'));
    } else if (stepName.startsWith('AddAccess_')) {
      this.addAccessController.handleAccessAttributeMappingSubmit(
        formData,
        isValid
      );
    } else if (stepName === 'AddAccessConfirmation') {
      // This is called by the addAccessAttributeMappingLogic hook
      this.addAccessController.showAddAcessConfirmation(formData);
    } else if (stepName === 'RemoveAccessConfirmation') {
      // This is called by the removeAccessLogic hook
      this.removeAccessController.showRemoveAccessConfirmation(formData);
    } else if (stepName === 'RemoveAccess') {
      this.removeAccessController.handleRemoveAccessAttributeMappingSubmit(
        formData,
        isValid
      );
    } else {
      this.dispatch(addCompletedStep(stepName));
    }
  }

  handleButtonClick(
    buttonId: string,
    buttonContext?: { label?: string }
  ): void {
    // Clear success/error messages when any button is clicked
    this.clearSuccessErrorMessages();

    // Handle AddAccess/RemoveAccess specific buttons first
    if (buttonId === 'addaccess_prompt_yes') {
      this.addAccessController.handleAddAccessPromptYes();

      return;
    }

    if (buttonId === 'addaccess_upload_yes') {
      this.addAccessController.handleAddAccessUploadYes();

      return;
    }

    if (buttonId === 'handle_No_add_access') {
      this.addAccessController.handleAddAccessNo().catch(() => {
        showError(this.dispatch, 'Failed to process add access action');
      });

      return;
    }

    if (buttonId === 'removeaccess_prompt_yes') {
      this.removeAccessController.handleRemoveAccessPromptYes();

      return;
    }

    if (buttonId === 'removeaccess_upload_yes') {
      this.removeAccessController.handleRemoveAccessUploadYes();

      return;
    }

    if (buttonId === 'handle_No_remove_access') {
      this.removeAccessController.handleRemoveAccessNo().catch(() => {
        showError(this.dispatch, 'Failed to process remove access action');
      });

      return;
    }

    // Handle undefined buttonId case
    if (!buttonId) {
      if (buttonContext?.label === 'No') {
        // Check if we're in remove access flow
        const state = this.getState() as RootState;
        const { activeStep } = state.copilot.navigation;
        const hasRemoveAccessMapping = state.copilot.messages.some(
          (msg) =>
            msg.subType === 'removeAccess' ||
            msg.subType === 'remove_access_form'
        );

        if (activeStep === 'RemoveAccess' || hasRemoveAccessMapping) {
          // Handle remove access "No" button
          this.removeAccessController.handleRemoveAccessNo().catch(() => {
            showError(this.dispatch, 'Failed to process remove access action');
          });
        } else {
          // Handle add access "No" button (original behavior)
          this.showNextEntitlementForm();
        }

        return;
      }
    }

    // Handle previous step edit responses
    if (buttonId === 'continue_with_instructions') {
      handleContinueWithInstructions(
        this.dispatch,
        this.getState,
        this.services
      ).catch(() => {
        showError(this.dispatch, 'Failed to continue with instructions');
      });

      return;
    }

    if (buttonId === 'modify_instructions') {
      handleModifyInstructions(this.dispatch, this.getState);

      return;
    }

    // Handle addAccessConfirmationNo button
    if (buttonId === 'addAccessConfirmationNo') {
      this.showNextEntitlementForm();

      return;
    }

    // Handle navigation back to listing with parameter
    if (buttonId.startsWith('handleNavigationBackToListing:')) {
      const userResponse = buttonId.split(':')[1] || 'Yes,Go Ahead';

      this.handleNavigationBackToListing(userResponse);

      return;
    }

    // Handle "I want to add more" button
    if (buttonId === 'handleFinalCall') {
      this.confirmationService.handleFinalCall();

      return;
    }

    // Delegate to existing button handler for other buttons
    handleButtonClick(
      buttonId,
      buttonContext,
      this.dispatch,
      this.getState,
      this.services,
      {
        showFinalConfirmation: () => {
          this.showFinalConfirmation();
        },
        handleConnectionHelpNo: async () => {
          await handleStepHelpNo(
            this.dispatch,
            this.getState,
            this.services,
            STEP_CONFIGS.CONNECTION
          );
        },
        handleAllAccountHelpNo: async () => {
          await handleAccountHelpNo(
            this.dispatch,
            this.getState,
            this.services
          );
        },
        handleAddAccessUploadYes: () => {
          this.addAccessController.handleAddAccessUploadYes();
        },
        handleRemoveAccessUploadYes: () => {
          this.removeAccessController.handleRemoveAccessUploadYes();
        },
        saveUseCaseConfiguration: this.services.saveUseCaseConfiguration,
      },
      this // Pass controller instance
    );
  }

  async handleUserPrompt(prompt: string): Promise<void> {
    const state = this.getState() as {
      copilot?: {
        messages?: { subType?: string }[];
        navigation?: { activeStep?: string };
      };
    };

    const messages = state.copilot?.messages || [];
    const activeStep = state.copilot?.navigation?.activeStep;

    // Check for AddAccess/RemoveAccess flows
    const hasAccessAttributeMapping = messages.some(
      (msg) => msg.subType === 'accessAttributeMapping'
    );
    const hasRemoveAccessMapping = messages.some(
      (msg) =>
        msg.subType === 'removeAccess' || msg.subType === 'remove_access_form'
    );

    // Find the most recent form message to determine current flow
    const lastFormMessage = messages
      .slice()
      .reverse()
      .find(
        (
          msg
        ): msg is {
          subType?: string;
          formData?: { entitlementIndex?: number };
        } =>
          msg.subType === 'accessAttributeMapping' ||
          msg.subType === 'removeAccess' ||
          msg.subType === 'remove_access_form'
      );

    const isCurrentlyInRemoveAccess =
      activeStep === 'RemoveAccess' ||
      (activeStep?.includes('RemoveAccess') &&
        (lastFormMessage?.subType === 'removeAccess' ||
          lastFormMessage?.subType === 'remove_access_form'));

    const isCurrentlyInAddAccess =
      activeStep === 'AddAccess' ||
      activeStep?.startsWith('AddAccess_') ||
      (hasAccessAttributeMapping &&
        lastFormMessage?.subType === 'accessAttributeMapping');

    // handle remove access step with prompt (including entitlement mapping) - CHECK FIRST
    if (isCurrentlyInRemoveAccess && !isCurrentlyInAddAccess) {
      this.removeAccessController
        .handleRemoveAccessPromptWithIndex(prompt)
        .catch(() => {
          showError(this.dispatch, 'Failed to process remove access prompt');
        });

      return;
    }

    // handle add access step with prompt (including entitlement mapping)
    if (isCurrentlyInAddAccess) {
      this.addAccessController
        .handleAddAccessPromptWithIndex(prompt)
        .catch(() => {
          showError(this.dispatch, 'Failed to process add access prompt');
        });

      return;
    }

    // Handle connection step with user prompt
    if (activeStep === 'Connection') {
      const currentState = this.getState() as {
        copilot?: {
          ui?: { endpointKey?: string };
          endpointKey?: string;
          stepFormData?: Record<string, Record<string, unknown>>;
        };
      };
      const endpointKey =
        currentState.copilot?.ui?.endpointKey ||
        currentState.copilot?.endpointKey;

      if (endpointKey) {
        // Edit mode: Call saveUseCaseConfiguration directly
        const stepFormData =
          currentState.copilot?.stepFormData?.Connection || {};
        const applicationtype =
          currentState.copilot?.stepFormData?.General?.[
            'securitysystems.systemtype'
          ] || 'CUA';

        // Store prompt after successful save, not before
        const connectionPayload = buildConnectionPayload(stepFormData, prompt);

        try {
          const result = await this.services.saveUseCaseConfiguration({
            applicationtype: String(applicationtype),
            endpointKey: Number(endpointKey),
            body: connectionPayload,
          });

          // Check if result indicates failure
          if (result && typeof result === 'object') {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            const resultObj = result as any;

            // Check for RTK Query error structure or API error response

            if (
              resultObj.error ||
              resultObj.status >= 400 ||
              (resultObj.data && resultObj.data.status >= 400)
            ) {
              throw new Error('Save configuration failed');
            }
          }

          // Store connection prompt only after successful save
          this.dispatch(setConnectionPrompt(prompt));

          // Test connection after saving
          const generalData = currentState.copilot?.stepFormData?.General || {};
          const endpointName = String(
            generalData['securitysystems.systemname'] || ''
          );

          try {
            await this.services.testConnection({
              endpointName,
            });
          } catch (testError) {
            // Clear connection prompt on test failure
            this.dispatch(setConnectionPrompt(''));
            showError(
              this.dispatch,
              'Connection test failed. Please verify your connection details.'
            );

            return;
          }

          // Update parsedTemplates with the new prompt
          const stateAfterSave = this.getState() as {
            copilot?: CopilotState;
          };
          const currentParsedTemplates =
            stateAfterSave.copilot?.parsedTemplates || {};
          const currentConnection = currentParsedTemplates.Connection as
            | unknown[]
            | undefined;

          // Connection is an array with instructions property
          const updatedConnection: unknown[] & { instructions?: string } = (
            Array.isArray(currentConnection) ? [...currentConnection] : []
          ) as unknown[] & { instructions?: string };

          updatedConnection.instructions = prompt;

          const updatedParsedTemplates = {
            ...currentParsedTemplates,
            Connection: updatedConnection,
          };

          this.dispatch(setParsedTemplates(updatedParsedTemplates));

          this.dispatch(addCompletedStep('Connection'));
          this.dispatch(removeSkippedStep('Connection'));

          this.dispatch(
            addMessage({
              id: `success-${Date.now()}`,
              type: 'ai_response',
              subType: 'success',
              text: 'Connection details updated successfully.',
              timestamp: Date.now(),
            })
          );
        } catch (error) {
          // Check if there was a previous prompt
          const currentStateOnCatch = this.getState() as {
            copilot?: CopilotState;
          };
          const existingPrompt =
            currentStateOnCatch.copilot?.connectionPrompt || '';

          // Only clear if no previous prompt exists
          if (!existingPrompt) {
            this.dispatch(setConnectionPrompt(''));
          }
          // If previous prompt exists, keep it (do nothing)

          this.dispatch(
            addMessage({
              id: `error-${Date.now()}`,
              type: 'ai_response',
              subType: 'error',
              text: 'Failed to save connection details. Please try again.',
              timestamp: Date.now(),
            })
          );
        }
      } else {
        // Create mode: Use existing flow
        handleStepHelpYes(
          this.dispatch,
          this.getState,
          this.services,
          STEP_CONFIGS.CONNECTION,
          prompt
        ).catch(() => {
          // Clear connection prompt on create mode failure
          this.dispatch(setConnectionPrompt(''));
          showError(this.dispatch, 'Failed to process connection prompt');
        });
      }
    }
    // Handle account steps with user prompt
    else if (
      activeStep &&
      [
        'CreateAccount',
        'UpdateAccount',
        'EnableAccount',
        'DisableAccount',
        'RemoveAccount',
      ].includes(activeStep)
    ) {
      // Check if we're in edit mode for account steps
      const currentState = this.getState() as {
        copilot?: {
          ui?: { endpointKey?: string };
          stepFormData?: Record<string, Record<string, unknown>>;
        };
      };
      const endpointKey = currentState.copilot?.ui?.endpointKey;

      if (endpointKey) {
        // Edit mode for any account step: Call saveUseCaseConfiguration directly
        const jsonFieldMap: Record<string, string> = {
          CreateAccount: 'externalconnattvalue.CreateAccountJSON',
          UpdateAccount: 'externalconnattvalue.UpdateAccountJSON',
          EnableAccount: 'externalconnattvalue.EnableAccountJSON',
          DisableAccount: 'externalconnattvalue.DisableAccountJSON',
          RemoveAccount: 'externalconnattvalue.RemoveAccountJSON',
        };

        const jsonField = jsonFieldMap[activeStep];
        const formData = currentState.copilot?.stepFormData?.[activeStep] ?? {};
        const accountJSON = formData[jsonField];

        if (jsonField && typeof accountJSON === 'string') {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-argument
          await saveAccountConfigurationForEdit(
            this.services,
            this.dispatch,
            this.getState,
            activeStep as
              | 'CreateAccount'
              | 'UpdateAccount'
              | 'EnableAccount'
              | 'DisableAccount'
              | 'RemoveAccount',
            accountJSON,
            Number(endpointKey),
            prompt
          );
        }
      } else {
        // Create mode or other account steps: Use existing flow
        handleAccountHelpYes(
          this.dispatch,
          this.getState,
          this.services,
          prompt
        ).catch(() => {
          showError(this.dispatch, 'Failed to process account prompt');
        });
      }
    }
  }

  private showFirstEntitlementForm() {
    const state = this.getState() as {
      copilot?: {
        entitlementIteration?: {
          entitlements: { id: number; value: string }[];
        };
      };
    };
    const entitlements =
      state.copilot?.entitlementIteration?.entitlements ?? [];

    if (entitlements.length > 0) {
      // Clear any existing loader messages before showing new ones
      this.dispatch(removeMessagesBySubType('accessAttributeMappingLoader'));

      this.dispatch(
        addMessage({
          id: `moving-to-add-access-${Date.now()}`,
          type: 'ai_response',
          subType: 'accessAttributeMapping',
          timestamp: Date.now(),
          loader: true,
        })
      );
      this.showEntitlementForm(0, entitlements[0].value);
    } else {
      // No entitlements found
    }
  }

  private showEntitlementForm(index: number, entitlementName: string) {
    const state = this.getState() as RootState;

    // Only check for exact same entitlement form, not any entitlement form
    const exactFormExists =
      state.copilot.messages.some(
        (msg) =>
          msg.type === 'step_form' &&
          msg.addAccessEntitlementIndex === index &&
          msg.stepName === `AddAccess_${index}`
      ) ?? false;

    // In create mode, don't show duplicate forms
    // In edit mode, allow re-showing forms for user interaction
    const isCurrentlyInEditMode = state.copilot.ui.mode === 'edit';

    if (exactFormExists && !isCurrentlyInEditMode) {
      return; // Don't show duplicate form in create mode
    }

    const entitlements = state.copilot.entitlementIteration.entitlements.map(
      (ent) => ent.value
    );

    const formMessage = {
      id: `form-entitlement-${index}-${Date.now()}`,
      type: 'step_form' as const,
      subType: 'accessAttributeMapping' as const,
      timestamp: Date.now(),
      heading: `Access Attribute Mapping - ${entitlementName}`,
      fields: state.copilot.parsedTemplates.AddAccess
        ? [
            {
              ...state.copilot.parsedTemplates.AddAccess,
              entitlementIndex: index,
            },
          ]
        : [],
      formData: { entitlementIndex: index },
      isCollapseable: false,
      buttonLabel: 'Continue',
      stepName: `AddAccess_${index}`,
      parentEntitlementIndex: index,
      entitlementType: 'addAccess' as const,
      addAccessEntitlementIndex: index,
      removeAccessEntitlementIndex: -1,
    };

    setTimeout(() => {
      // Set the active step to the current entitlement
      this.dispatch(setActiveStep(`AddAccess_${index}`));

      if (isCurrentlyInEditMode) {
        //   // Enhanced cleanup for edit mode - remove ALL previous messages to show only current entitlement
        //   this.dispatch(removeMessagesBySubType('addAccessEditPhase'));
        this.dispatch(removeMessagesBySubType('add_access_form'));
        //   this.dispatch(removeMessagesBySubType('userResponseNo'));
        //   this.dispatch(removeMessagesBySubType('userResponseYes'));
        //   this.dispatch(removeMessagesBySubType('connectionStepAssistInfo'));
        //   this.dispatch(removeMessagesBySubType('provideAdditionalInfo'));
        //   this.dispatch(removeMessagesBySubType('file_display'));
        //   this.dispatch(removeMessagesBySubType('analyzing'));
      }

      // Remove loader messages before showing new entitlement form
      const currentState = this.getState() as {
        copilot?: {
          messages?: {
            id?: string;
            subType?: string;
            loader?: boolean;
            type?: string;
          }[];
        };
      };

      const currentMessages = currentState.copilot?.messages || [];

      const accessMappingLoaders = currentMessages.filter(
        (msg: { subType?: string; loader?: boolean }) =>
          msg.subType === 'accessAttributeMapping' && msg.loader === true
      );

      const attributeMappingMessages = currentMessages.filter(
        (msg: { subType?: string }) => msg.subType === 'attributeMappingNext'
      );

      [...accessMappingLoaders, ...attributeMappingMessages].forEach((msg) => {
        if (msg.id) {
          this.dispatch(removeMessage(msg.id));
        }
      });

      // Show the entitlement form
      this.dispatch(addMessage(formMessage));
    }, 2000);
  }

  private showNextEntitlementForm() {
    // Call AddAccessController's handleAddAccessNo to make API call and handle next entitlement
    this.addAccessController.handleAddAccessNo().catch(() => {
      showError(this.dispatch, 'Failed to process next entitlement');
    });
  }

  // AddAccess confirmation moved to AddAccessController

  // RemoveAccess methods moved to RemoveAccessController

  // AddAccess Three Button Handlers - moved to AddAccessController

  // AddAccess methods moved to AddAccessController

  public handleFileUpload(file: File): void {
    const state = this.getState() as {
      copilot?: {
        messages?: { subType?: string }[];
        navigation?: { activeStep?: string };
      };
    };

    const messages = state.copilot?.messages || [];
    const activeStep = state.copilot?.navigation?.activeStep;

    const hasAccessAttributeMapping = messages.some(
      (msg) => msg.subType === 'accessAttributeMapping'
    );
    const hasRemoveAccessMapping = messages.some(
      (msg) =>
        msg.subType === 'removeAccess' || msg.subType === 'remove_access_form'
    );

    if (activeStep === 'AddAccess' || hasAccessAttributeMapping) {
      this.addAccessController.handleAddAccessFile(file);
    } else if (activeStep === 'RemoveAccess' || hasRemoveAccessMapping) {
      this.removeAccessController.handleRemoveAccessFile(file);
    }
  }

  // RemoveAccess button handlers moved to RemoveAccessController

  // AddAccess methods moved to AddAccessController

  private moveToRemoveAccess(): void {
    const state = this.getState() as {
      copilot?: {
        parsedTemplates?: {
          RemoveAccess?: unknown;
        };
        entitlementIteration?: {
          entitlements: { id: number; value: string }[];
        };
        messages?: {
          type?: string;
          subType?: string;
        }[];
      };
    };

    // Check if RemoveAccess form already exists
    const removeAccessFormExists =
      state.copilot?.messages?.some(
        (msg) => msg.type === 'step_form' && msg.subType === 'removeAccess'
      ) ?? false;

    if (removeAccessFormExists) {
      return; // Don't show duplicate RemoveAccess form
    }

    this.dispatch(addCompletedStep('AddAccess'));
    this.dispatch(setActiveStep('RemoveAccess'));

    this.dispatch(
      addMessage({
        id: `remove-access-${Date.now()}`,
        type: 'ai_response',
        subType: 'removeAccessNextStep',
        loader: true,
        timestamp: Date.now(),
      })
    );

    setTimeout(() => {
      this.dispatch(removeMessagesBySubType('removeAccessNextStep'));

      this.dispatch(
        addMessage({
          id: `remove-access-mapping-${Date.now()}`,
          type: 'step_form',
          subType: 'removeAccess',
          timestamp: Date.now(),
          heading: 'Remove Access Attribute Mapping',
          fields: state.copilot?.parsedTemplates?.RemoveAccess
            ? [
                {
                  ...state.copilot.parsedTemplates.RemoveAccess,
                },
              ]
            : [],
          formData: { entitlementIndex: 0 },
          isCollapseable: false,
          buttonLabel: 'Continue',
          stepName: 'RemoveAccess',
          // Add entitlement grouping like AddAccess
          parentEntitlementIndex: 0,
          entitlementType: 'removeAccess',
          addAccessEntitlementIndex: -1,
          removeAccessEntitlementIndex: 0,
        })
      );
    }, 2000);

    removeMessagesBySubType('removeAccessNextStep');
  }

  private showFinalConfirmation(): void {
    this.confirmationService.showConfirmationStep();
  }

  /**
   * Show step form by name for skip navigation
   */
  private showStepFormByName(stepName: string): void {
    // Map step names to their show methods (similar to V1 approach)
    const stepShowMethods: Record<string, () => void> = {
      UpdateAccount: () => this.showUpdateAccountForm(),
      EnableAccount: () => this.showEnableAccountForm(),
      DisableAccount: () => this.showDisableAccountForm(),
      RemoveAccount: () => this.showRemoveAccountForm(),
      AddAccess: () => this.showAddAccessForm(),
    };

    const showMethod = stepShowMethods[stepName];

    if (showMethod) {
      showMethod();
    }
  }

  // Step form show methods (simplified versions)
  private showUpdateAccountForm(): void {
    const stepConfig = STEP_CONFIGS.UpdateAccount;

    if (stepConfig) {
      const state = this.getState() as { copilot?: CopilotState };
      const allStepFormData = state.copilot?.stepFormData || {};

      showStepForm(this.dispatch, allStepFormData, 'UpdateAccount', false);
    }
  }

  private showEnableAccountForm(): void {
    const stepConfig = STEP_CONFIGS.EnableAccount;

    if (stepConfig) {
      const state = this.getState() as { copilot?: CopilotState };
      const allStepFormData = state.copilot?.stepFormData || {};

      showStepForm(this.dispatch, allStepFormData, 'EnableAccount', false);
    }
  }

  private showDisableAccountForm(): void {
    const stepConfig = STEP_CONFIGS.DisableAccount;

    if (stepConfig) {
      const state = this.getState() as { copilot?: CopilotState };
      const allStepFormData = state.copilot?.stepFormData || {};

      showStepForm(this.dispatch, allStepFormData, 'DisableAccount', false);
    }
  }

  private showRemoveAccountForm(): void {
    const stepConfig = STEP_CONFIGS.RemoveAccount;

    if (stepConfig) {
      const state = this.getState() as { copilot?: CopilotState };
      const allStepFormData = state.copilot?.stepFormData || {};

      showStepForm(this.dispatch, allStepFormData, 'RemoveAccount', false);
    }
  }

  private showAddAccessForm(): void {
    const state = this.getState() as { copilot?: CopilotState };
    const allStepFormData = state.copilot?.stepFormData || {};

    showStepForm(this.dispatch, allStepFormData, 'AddAccess', false);
  }

  // Skip Methods - Delegate to SkipService
  skipToFinalConfirmation(): void {
    this.skipService.skipToFinalConfirmation();
  }

  skipAddAccess(): void {
    this.skipService.skipAddAccess();
  }

  skipFromRemoveAccess(entitlementIndex = 0): void {
    this.skipService.skipFromRemoveAccess(entitlementIndex);
  }

  handleSkip(
    currentStep: string,
    nextStep: string,
    entitlementIndex?: number
  ): void {
    this.skipService.handleSkip(currentStep, nextStep, entitlementIndex);
  }

  private handleNavigationBackToListing(userResponse: string): void {
    // Add user response
    this.dispatch(
      addMessage({
        id: `user-response-${Date.now()}`,
        type: 'user_response',
        subType: 'userResponseYes',
        text: userResponse,
        timestamp: Date.now(),
      })
    );

    // Add final confirmation message
    this.dispatch(
      addMessage({
        id: Date.now().toString(),
        type: 'ai_response',
        subType: 'finalConfirmationRedirection',
        loader: true,
        text: "Let's goooo! Onboarding your application",
        timestamp: Date.now(),
      })
    );

    // Navigate to integrations page
    setTimeout(() => {
      this.history.push('/integrations');
    }, 1500);
  }
}
